import React, { useState, useEffect, useLayoutEffect } from "react";
import SelectedCriteria from "./SelectedCriteria";
import SpecialtySelection from "./SpecialtySelection";
import ProfileSettings from "./ProfileSettings";
import YourFavoriteResidencies from "./YourFavoriteResidencies";
import { signOut } from "firebase/auth";
import { auth } from "./firebase";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";

function Dashboard() {
  const [activeTab, setActiveTab] = useState("criteria");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      const stored = localStorage.getItem("dashboardDarkMode");
      return stored === null ? true : stored === "true";
    } catch {
      return true;
    }
  });
  const { user, userData, loading: isLoading, refreshUserData, hasAnySpecialtyAccess } = useAuth();
  const [mounted, setMounted] = useState(false);
  const [tabTransitioning, setTabTransitioning] = useState(false);
  const navigate = useNavigate();

  useLayoutEffect(() => {
    const root = document.documentElement;
    if (isDarkMode) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [isDarkMode]);
  useEffect(() => {
    try {
      localStorage.setItem("dashboardDarkMode", isDarkMode ? "true" : "false");
    } catch (_) {}
  }, [isDarkMode]);

  useEffect(() => {
  const handleNavigation = () => {
    if (user) {
      refreshUserData(true);
    }
  };

  window.addEventListener('pageshow', handleNavigation);
  return () => window.removeEventListener('pageshow', handleNavigation);
}, [user, refreshUserData]);

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);


  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleTabChange = (tab) => {
    if (tab === "observerships") {
      navigate("/observerships");
      return;
    }
    if (tab === activeTab) {
      setIsSidebarOpen(false);
      return;
    }
    setTabTransitioning(true);
    setTimeout(() => {
      setActiveTab(tab);
      setIsSidebarOpen(false);
      requestAnimationFrame(() => requestAnimationFrame(() => setTabTransitioning(false)));
    }, 120);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "criteria":
        return <SelectedCriteria />;
      case "lists":
        return <SpecialtySelection />;
      case "favorites":
        return <YourFavoriteResidencies />;
      case "profile":
        return <ProfileSettings />;
      default:
        return <SelectedCriteria />;
    }
  };

  const getPageTitle = () => {
    switch (activeTab) {
      case "criteria":
        return "Selection Criteria";
      case "lists":
        return "My Residency Lists";
      case "favorites":
        return "Your Favorite Residencies";
      case "profile":
        return "Profile Settings";
      default:
        return "Dashboard";
    }
  };

if (isLoading) {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif' }}>
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-gray-200 dark:border-gray-600 border-t-gray-500 dark:border-t-gray-400 rounded-full animate-spin mx-auto mb-3" />
        <p className="text-[15px] text-gray-500 dark:text-gray-400">Loading...</p>
      </div>
    </div>
  );
}

// Add this check for when user exists but userData is still loading:
if (user && !userData) {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif' }}>
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-gray-200 dark:border-gray-600 border-t-gray-500 dark:border-t-gray-400 rounded-full animate-spin mx-auto mb-3" />
        <p className="text-[15px] text-gray-500 dark:text-gray-400">Loading profile...</p>
      </div>
    </div>
  );
}

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif' }}>
      {/* Mobile Header */}
      <div className="lg:hidden bg-white dark:bg-gray-900 border-b border-black/6 dark:border-gray-700">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center">
            <img src="/images/logo.png" alt="Residency Companion Logo" className="w-8 h-8 mr-3 rounded-lg" />
            <h1 className="text-[18px] font-semibold text-black dark:text-white tracking-tight">
              Residency Companion
            </h1>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setIsDarkMode((prev) => !prev)}
              className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 text-gray-700 dark:text-gray-300"
              title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDarkMode ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
              )}
            </button>
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 text-gray-700 dark:text-gray-300"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={
                    isSidebarOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"
                  }
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div
          className={`${
            isSidebarOpen ? "translate-x-0" : "-translate-x-full"
          } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-900 border-r border-black/6 dark:border-gray-700`}
          style={{
            opacity: mounted ? 1 : 0,
            transition: 'transform 300ms ease, opacity 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.04s'
          }}
        >
          {/* User Profile Section */}
          <div className="pt-12 px-6 pb-6">
            <div className="flex items-center">
              <div className="relative flex-shrink-0">
                <div className="w-12 h-12 bg-black dark:bg-gray-700 rounded-xl flex items-center justify-center text-white font-semibold text-lg">
                  {userData?.name
                    ? userData.name.charAt(0).toUpperCase()
                    : user?.email?.charAt(0).toUpperCase() || "U"}
                </div>
                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-gray-400 dark:bg-green-500 rounded-full border-2 border-white dark:border-gray-900" />
              </div>
              <div className="ml-3 min-w-0 flex-1">
                <div className="flex items-center space-x-2">
                  <p className="font-semibold text-black dark:text-white truncate text-[15px]">
                    {userData?.name || "User"}
                  </p>
                  {hasAnySpecialtyAccess() && (
                    <span className="px-2 py-0.5 bg-black dark:bg-blue-600 text-white text-[11px] font-medium rounded-full uppercase tracking-wide">
                      Premium
                    </span>
                  )}
                </div>
                <p className="text-[13px] text-gray-500 dark:text-gray-400 truncate mt-0.5" title={user?.email}>
                  {user?.email}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 p-4">
            <div className="space-y-1">
              {[
                { id: "criteria", icon: "🧾", label: "Selection Criteria" },
                { id: "lists",    icon: "📋", label: "My Residency Lists" },
                { id: "favorites",icon: "❤️", label: "Favorite Residencies" },
                { id: "observerships", icon: "🏥", label: "Observerships" },
                { id: "profile",  icon: "👤", label: "Profile Settings" },
              ].map((item, i) => (
                <button
                  key={item.id}
                  onClick={() => handleTabChange(item.id)}
                  style={{
                    opacity: mounted ? 1 : 0,
                    transform: mounted ? 'translateX(0)' : 'translateX(-8px)',
                    transition: `opacity 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${0.06 + i * 0.05}s, transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${0.06 + i * 0.05}s`
                  }}
                  className={`w-full group flex items-center px-4 py-3 text-left rounded-xl transition-all duration-200 active:scale-[0.97] ${
                    activeTab === item.id
                      ? "bg-gray-400 dark:bg-gray-600 text-white border-l-2 border-l-blue-500 dark:border-l-blue-400"
                      : "text-gray-800 dark:text-gray-200 hover:bg-black/[0.04] dark:hover:bg-white/10 border border-transparent"
                  }`}
                >
                  <span className={`mr-3 text-lg transition-transform duration-200 flex-shrink-0 ${activeTab === item.id ? "" : "group-hover:scale-110"}`}>
                    {item.icon}
                  </span>
                  <span className="font-medium truncate">{item.label}</span>
                </button>
              ))}
            </div>
          </nav>

          {/* Dark mode toggle (desktop) */}
          <div className="px-4 pb-2">
            <button
              type="button"
              onClick={() => setIsDarkMode((prev) => !prev)}
              className="w-full group flex items-center px-4 py-3 text-left rounded-xl text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 active:scale-[0.97]"
              title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              <span className="mr-3 text-lg transition-transform duration-200 group-hover:scale-110 flex-shrink-0">
                {isDarkMode ? "☀️" : "🌙"}
              </span>
              <span className="font-medium truncate">{isDarkMode ? "Light mode" : "Dark mode"}</span>
            </button>
          </div>
          {/* Logout Button */}
          <div className="p-4 border-t border-black/8 dark:border-gray-700">
            <button
              onClick={handleLogout}
              className="w-full group flex items-center px-4 py-3 text-left rounded-xl text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 active:scale-[0.97]"
            >
              <span className="mr-3 text-lg transition-transform duration-200 group-hover:scale-110 flex-shrink-0">
                🚪
              </span>
              <span className="font-medium truncate">Sign Out</span>
            </button>
          </div>
        </div>

{/* Quick Support - blue outline */}
<div className="fixed bottom-6 left-6 z-50">
  <div className="mb-2">
    <h3 className="text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-widest text-center">Help & Support</h3>
  </div>
  <div className="bg-white dark:bg-gray-800 rounded-full border-2 border-blue-500/40 dark:border-blue-400/50 p-1.5 shadow-sm">
    <div className="flex space-x-2">
      <a
        href="mailto:info@revisableapp.com"
        className="border-2 border-blue-500/50 dark:border-blue-400/60 hover:border-blue-500 dark:hover:border-blue-400 text-gray-700 dark:text-gray-300 p-4 rounded-full transition-all duration-200 hover:bg-black/[0.02] dark:hover:bg-white/10"
        title="Email Support"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      </a>
      <a
        href="https://wa.me/919650500689"
        target="_blank"
        rel="noopener noreferrer"
        className="border-2 border-blue-500/50 dark:border-blue-400/60 hover:border-blue-500 dark:hover:border-blue-400 text-gray-700 dark:text-gray-300 p-4 rounded-full transition-all duration-200 hover:bg-black/[0.02] dark:hover:bg-white/10"
        title="WhatsApp Support"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.512z"/>
        </svg>
      </a>
    </div>
  </div>
  <div className="absolute -top-10 left-0 opacity-0 hover:opacity-100 transition-opacity duration-200 pointer-events-none">
    <div className="bg-black dark:bg-gray-700 text-white dark:text-gray-100 text-[11px] font-medium rounded-lg px-3 py-2 whitespace-nowrap">
      Email or WhatsApp
      <div className="absolute -bottom-1.5 left-5 border-[6px] border-transparent border-t-black dark:border-t-gray-700"></div>
    </div>
  </div>
</div>
        {/* Main Content */}
        <div
          className="flex-1"
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(6px)',
            transition: 'opacity 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s'
          }}
        >
          {/* Main Header */}
          <div className="bg-white dark:bg-gray-900 backdrop-blur-sm border-b border-black/8 dark:border-gray-700">
            <div className="px-6 lg:px-8 py-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-semibold text-black dark:text-white tracking-[-0.02em]">
                    {getPageTitle()}
                  </h1>
                  <p className="text-gray-500 dark:text-gray-400 mt-1 text-[15px]">
                    {activeTab === "criteria" &&
                      "Set your preferences for residency program selection"}
                    {activeTab === "lists" &&
                      "View and manage your college specialty lists"}
                    {activeTab === "favorites" &&
                      "Your saved residency programs for quick access"}
                    {activeTab === "observerships" &&
                      "Browse US clinical observerships and elective opportunities"}
                    {activeTab === "profile" &&
                      "Update your profile information and preferences"}
                  </p>
                </div>

                <div className="hidden md:block">
                  <div className="px-4 py-2 rounded-full border-2 border-blue-500/40 dark:border-blue-400/50 bg-white dark:bg-gray-800">
                    <span className="text-[13px] font-medium text-black dark:text-white">
                      Welcome {userData?.name?.split(" ")[0]} 👋
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6 lg:p-8">
            <div className="max-w-6xl mx-auto">
              {/* Welcome Banner (only on criteria tab) */}
              {activeTab === "criteria" && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 mb-6 border border-black/8 dark:border-gray-700 border-l-2 border-l-black/20 dark:border-l-gray-600">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-black dark:text-white mb-2 tracking-[-0.02em]">
                        Find Your Perfect Medical Residency
                      </h2>
                      <p className="text-gray-500 dark:text-gray-400 text-[15px]">
                        Set your preferences to get personalized recommendations
                      </p>
                    </div>
                    <div className="hidden md:block">
                      <div className="w-16 h-16 rounded-2xl flex items-center justify-center border border-black/10 dark:border-gray-600 bg-black/[0.02] dark:bg-white/5">
                        <span className="text-3xl">🎯</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Favorites Welcome Banner */}
              {activeTab === "favorites" && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 mb-6 border border-black/8 dark:border-gray-700 border-l-2 border-l-black/20 dark:border-l-gray-600">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-black dark:text-white mb-2 tracking-[-0.02em]">
                        Your Saved Residency Programs
                      </h2>
                      <p className="text-gray-500 dark:text-gray-400 text-[15px]">
                        Quick access to programs you're most interested in
                      </p>
                    </div>
                    <div className="hidden md:block">
                      <div className="w-16 h-16 rounded-2xl flex items-center justify-center border border-black/10 dark:border-gray-600 bg-black/[0.02] dark:bg-white/5">
                        <span className="text-3xl">❤️</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab Content - lists tab uses same dark base as SpecialtySelection for consistent tile colours */}
              <div
                className={`rounded-2xl border border-black/8 dark:border-gray-700 ${activeTab === "lists" ? "bg-white dark:bg-gray-900" : "bg-white dark:bg-gray-800"}`}
                style={{
                  opacity: tabTransitioning ? 0 : 1,
                  transform: tabTransitioning ? 'translateY(5px)' : 'translateY(0)',
                  transition: tabTransitioning
                    ? 'opacity 0.12s ease-out, transform 0.12s ease-out'
                    : 'opacity 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.02s, transform 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.02s'
                }}
              >
                <div className="p-6 lg:p-8">
                  {renderTabContent()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;